import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from './querykeys'
import ticketsApi, {
  CreateTicketRequest,
  CreateCommentRequest,
  UpdateCommentRequest
} from '../api/tickets.api'
import { UserAuth } from '../context/AuthContext'

export const useQueryGetTickets = (options?: { enabled?: boolean }) => {
  const { isAuthenticated } = UserAuth()
  
  return useQuery({
    enabled: (options?.enabled ?? true) && isAuthenticated,
    queryKey: [QueryKeys.GET_TICKETS],
    queryFn: async () => {
      const res = await ticketsApi.getAll()
      return res
    }
  })
}

export const useQueryGetMyTickets = (options?: { enabled?: boolean }) => {
  const { isAuthenticated } = UserAuth()
  
  return useQuery({
    enabled: (options?.enabled ?? true) && isAuthenticated,
    queryKey: [QueryKeys.GET_MY_TICKETS],
    queryFn: async () => {
      const res = await ticketsApi.getMyTickets()
      return res
    }
  })
}

export const useQueryGetTicketById = (
  ticketId: number | undefined,
  options?: { enabled?: boolean }
) => {
  const { isAuthenticated } = UserAuth()
  
  return useQuery({
    enabled: (options?.enabled ?? true) && isAuthenticated && !!ticketId,
    queryKey: [QueryKeys.GET_TICKET_BY_ID, ticketId],
    queryFn: async () => {
      if (!ticketId) return null
      const res = await ticketsApi.getById(ticketId)
      return res
    }
  })
}

export const useMutationCreateTicket = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async (data: CreateTicketRequest & { message?: string; files?: File[] }) => {
      const res = await ticketsApi.create(data)
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MY_TICKETS] })
    }
  })
}

export const useMutationAddComment = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({
      ticketId,
      data
    }: {
      ticketId: number
      data: CreateCommentRequest
    }) => {
      const res = await ticketsApi.addComment(ticketId, data)
      return res
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MY_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKET_BY_ID, variables.ticketId] })
    }
  })
}

export const useMutationUpdateComment = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({
      commentId,
      ticketId,
      data
    }: {
      commentId: number
      ticketId: number
      data: UpdateCommentRequest
    }) => {
      await ticketsApi.updateComment(commentId, data)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MY_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKET_BY_ID, variables.ticketId] })
    }
  })
}

export const useMutationDeleteComment = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({
      commentId,
      ticketId
    }: {
      commentId: number
      ticketId: number
    }) => {
      await ticketsApi.deleteComment(commentId)
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MY_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKET_BY_ID, variables.ticketId] })
    }
  })
}

export const useMutationSolveTicket = () => {
  const queryClient = useQueryClient()
  
  return useMutation({
    mutationFn: async ({
      ticketId,
      data
    }: {
      ticketId: number
      data: CreateCommentRequest
    }) => {
      const res = await ticketsApi.solveTicket(ticketId, data)
      return res
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_MY_TICKETS] })
      queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_TICKET_BY_ID, variables.ticketId] })
    }
  })
}
